// Sealed doors (B3 shortcuts, B4 gates — monorail-ciy / 4fj). A closed
// door is a wall (its plug is baked into the layout SDF); this system
// decides whether pressing on one opens it:
//
//   shortcut   — only from the far side (the Souls loop home)
//   adaptation — if the specimen owns the keyed power (the verb IS the key)
//   lock       — if the specimen carries the key item (deadpan hall pass)
//
// Every open is permanent and persisted. A refused press emits door.blocked
// once per door per life — the narrator names what the seal answers to,
// which is the whole telegraph.

import type { Entity } from '../components'
import type { EventBus } from '../events'
import type { DoorPlug } from '../dungeon/types'
import { getActiveDungeon } from '../dungeon/active'
import { powerLevel, type PowerId } from '../powers'
import { T } from '../tunables'

// Session-scoped: one blocked-bump event per door per life (the narrator
// dedupes further via oneShot).
const bumped = new WeakSet<DoorPlug>()

function canOpen(door: DoorPlug, player: Entity, side: number): boolean {
  switch (door.kind) {
    case 'shortcut':
      return side > 0.05
    case 'adaptation':
      return powerLevel(player, door.keyId as PowerId) > 0
    case 'lock':
      return (player.keys ?? []).includes(door.keyId ?? '')
  }
}

export function doorsSystem(
  player: Entity,
  events: EventBus,
  onOpened: (door: DoorPlug) => void,
): void {
  const layout = getActiveDungeon()
  if (!layout?.doors || !player.renderable) return

  for (const door of layout.doors) {
    if (door.open) continue
    const dx = player.position.x - door.x
    const dy = player.position.y - door.y
    const dist = Math.hypot(dx, dy)
    const touch = door.r + player.renderable.radius + T.dungeon.doorReach
    if (dist > touch) continue
    // Side sign: shortcut plugs open only from their far side; a home-side
    // press is the geography lesson.
    const side = dx * door.nx + dy * door.ny
    if (canOpen(door, player, side)) {
      door.open = true
      events.emit({ type: 'door.opened', edge: door.edge, kind: door.kind, x: door.x, y: door.y })
      onOpened(door)
    } else if (!bumped.has(door) && (door.kind !== 'shortcut' || side < -0.05)) {
      bumped.add(door)
      events.emit({ type: 'door.blocked', edge: door.edge, kind: door.kind, keyId: door.keyId })
    }
  }
}
